import React, { useState } from 'react';
import { Dialog, Button, Select, Textarea, Input, RadioGroup, Group } from '@economic/taco';

import { t } from '../../l10n/l10n';
import { Form, Field } from '../../Form';
import { ProgressDots } from '../../ui/progressDots/ProgressDots';
import { CompanyDeregisterData } from './types';

interface Props {
    deregisterData: CompanyDeregisterData;
    onError: (error: string) => void;
    onBack?: () => void;
    onDone: (
        reason: string,
        recommendationScaleScore: string,
        comments: string,
        competitorName: string,
        tellUsMore: string
    ) => void;
}

export default function StepFeedback({ deregisterData, onBack, onDone, onError }: Props) {
    const [comments, setComments] = useState('');
    const [competitorName, setCompetitorName] = useState('');
    const [tellUsMore, setTellUsMore] = useState('');

    const [reason, setReason] = useState('');
    const [reasonOfAccountingProgramChange, setReasonOfAccountingProgramChange] = useState('');

    function getMergedReason() {
        let result = reason;
        if (reasonOfAccountingProgramChange !== '') {
            result = reasonOfAccountingProgramChange;
        }
        return result;
    }

    function handleSubmit(values) {
        if (validate(values)) {
            const mergedReason = getMergedReason();
            onDone(mergedReason, values.recommendationScaleScore, comments, competitorName, tellUsMore);
        }
    }

    function validate(values) {
        if (reason === '') {
            onError(t('DEREGISTRATION_FORM_VALIDATION_PLEASE_CHOOSE_REASON'));
            return false;
        }
        if (reason === 'default' && reasonOfAccountingProgramChange === '') {
            onError(t('DEREGISTRATION_FORM_VALIDATION_PLEASE_CHOOSE_REASON'));
            return false;
        }

        if (values.recommendationScaleScore === '') {
            onError(t('DEREGISTRATION_FORM_VALIDATION_PLEASE_CHOOSE_RATING'));
            return false;
        }

        return true;
    }

    function getRecommendationScaleList() {
        const list = [{ text: t`DEREGISTRATION_FORM_SCETION_RATING_DEFAULT_ITEM`, value: '' }];
        deregisterData.recommendationScale.forEach(item => {
            list.push({ text: item.value, value: item.key });
        });
        return list;
    }

    const changeOfAccountingProgramReasonList = deregisterData.changeOfAccountingProgramReasons.map(item => {
        return { text: item.value, value: item.key };
    });

    return (
        <div>
            <div style={{ marginBottom: 15 }}>
                <ProgressDots stepsCount={2} currentStep={2} />
            </div>
            <Form onSubmit={handleSubmit} initialData={deregisterData} skipNotifications>
                <Dialog.Title>{t`FORM_COMPANY_DEREGISTER_FEEDBACK_TITLE`}</Dialog.Title>

                <div style={{ marginBottom: 15 }}>{t`FORM_COMPANY_DEREGISTER_FEEDBACK_CONTENT`}</div>
                <div className="company-deregister-dialog dropdown_label">{t`DEREGISTRATION_FORM_SCETION_TITLE_REASONS`}</div>
                <div>
                    <RadioGroup
                        name="reason"
                        value={reason}
                        onChange={value => {
                            setReason(String(value));

                            if (value === 'default') {
                                setReasonOfAccountingProgramChange('');
                            }
                        }}>
                        <RadioGroup.Item value="default">
                            {t`DEREGISTRATION_FORM_MAIN_REASONS_CHANGE_ACCOUNTING_PROGRAM`}
                        </RadioGroup.Item>
                        <RadioGroup.Item value="101">{t`DEREGISTRATION_FORM_MAIN_REASONS_COMPANY_CLOSING`}</RadioGroup.Item>
                        <RadioGroup.Item value="DEREGISTRATION_FORM_MAIN_REASONS_STARTING_ANOTHER_AGR">{t`DEREGISTRATION_FORM_MAIN_REASONS_STARTING_ANOTHER_AGR`}</RadioGroup.Item>
                        <RadioGroup.Item value="104">{t`DEREGISTRATION_FORM_MAIN_REASONS_TEST_AGR_FOR_DEV`}</RadioGroup.Item>
                    </RadioGroup>

                    {reason === 'default' && (
                        <div>
                            <div style={{ paddingBottom: '15px' }}>
                                <span className="company-deregister-dialog dropdown_label">{t`DEREGISTRATION_FORM_SCETION_REASONS_DEFAULT_ITEM`}</span>
                                <Select
                                    emptyValue={null}
                                    data={changeOfAccountingProgramReasonList}
                                    onChange={(event: any) => setReasonOfAccountingProgramChange(event.detail.value)}
                                    value={reasonOfAccountingProgramChange}
                                />
                            </div>
                            <div className="company-deregister-dialog textarea_container">
                                <span className="company-deregister-dialog dropdown_label">{t`DEREGISTRATION_FORM_TELL_US_COMPETITOR_NAME`}</span>
                                <Input
                                    name="competitorName"
                                    maxLength={100}
                                    onChange={event => setCompetitorName(event.target.value)}
                                />
                            </div>

                            {(reasonOfAccountingProgramChange === '107' || reasonOfAccountingProgramChange === '108') && (
                                <div className="company-deregister-dialog textarea_container">
                                    <span className="company-deregister-dialog dropdown_label">
                                        {reasonOfAccountingProgramChange === '107'
                                            ? t`DEREGISTRATION_FORM_WHAT_FUNCTIONALITY_MISSING`
                                            : t`DEREGISTRATION_FORM_WHO_ARE_YOU_CHANGING_TO`}
                                    </span>
                                    <Input
                                        maxLength={100}
                                        name="tellUsMore"
                                        onChange={event => setTellUsMore(event.target.value)}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="company-deregister-dialog textarea_container">
                    <span className="company-deregister-dialog dropdown_label">{t`DEREGISTRATION_FORM_COMMENTS_TEXT`}</span>
                    <Textarea
                        name="comments"
                        maxLength={200}
                        onChange={event => setComments(event.target.value)}
                        value={comments}
                    />
                </div>

                <div>
                    <span className="company-deregister-dialog dropdown_label">{t`FORM_COMPANY_DEREGISTER_FEEDBACK_RECOMMENDATION_SCALE_TEXT`}</span>
                    <Field.Dropdown name="recommendationScaleScore" options={getRecommendationScaleList()} initialValue="" />
                </div>

                <Dialog.Footer>
                    <Group>
                        {onBack && (
                            <Button appearance="ghost" onClick={onBack}>
                                {t('FORM_COMPANY_DEREGISTER_FEEDBACK_BACK')}
                            </Button>
                        )}
                        <Button appearance="primary" type="submit">
                            {t('FORM_COMPANY_DEREGISTER_FEEDBACK_NEXT')}
                        </Button>
                    </Group>
                </Dialog.Footer>
            </Form>
        </div>
    );
}
